import { createClient } from '@supabase/supabase-js';
import { logger } from './logger';
import { env, isValidConfig } from './env';
import { withRetry } from '../utils/retry';
import { Profile } from '../types';
import { isPublicRoute } from '../constants/routes';

// Helper to determine if we're in preview mode
export const isPreviewMode = () => {
  // Check if we're running in preview mode (no Supabase credentials)
  return !isValidConfig || window.location.search.includes('preview=true');
};

// Initialize Supabase client with proper error handling
const initSupabase = () => {
  if (isPreviewMode()) {
    logger.info('Running in preview mode - using mock data');
    return createMockClient();
  }

  try {
    if (!env.VITE_SUPABASE_URL || !env.VITE_SUPABASE_ANON_KEY) {
      logger.error('Supabase configuration missing');
      return createMockClient();
    }

    const client = createClient(env.VITE_SUPABASE_URL, env.VITE_SUPABASE_ANON_KEY, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storage: typeof window !== 'undefined' ? window.localStorage : undefined
      },
      global: {
        headers: { 
          'x-client-info': 'vzmo-web'
        }
      },
      realtime: {
        params: {
          eventsPerSecond: 10
        }
      }
    });

    // Add auth state listener
    client.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        // Clear any auth-dependent state
        window.localStorage.clear();
        window.sessionStorage.clear();
        
        // Only redirect to login if not on a public page
        const currentPath = window.location.pathname;
        if (!isPublicRoute(currentPath)) {
          window.location.href = '/login';
        }
      } else if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
        // Refresh app state when session changes
        window.dispatchEvent(new CustomEvent('supabase-auth-refresh'));
      }
    });

    return client;
  } catch (error) {
    logger.error('Failed to initialize Supabase client:', error);
    return createMockClient();
  }
};

// Create mock client for preview/fallback mode
const createMockClient = (): any => ({
  auth: {
    getUser: async () => ({
      data: { user: { id: 'preview-user', email: 'preview@example.com' } },
      error: null
    }),
    getSession: async () => ({
      data: { 
        session: {
          user: { id: 'preview-user', email: 'preview@example.com' as string },
          access_token: 'preview-token'
        }
      },
      error: null
    }),
    signOut: async () => ({ error: null }),
    onAuthStateChange: () => {
      return { unsubscribe: () => {} };
    },
    signInWithPassword: async () => ({
      data: {
        user: { id: 'preview-user', email: 'preview@example.com' },
        session: {
          user: { id: 'preview-user', email: 'preview@example.com' },
          access_token: 'preview-token'
        }
      },
      error: null
    }),
    signUp: async () => ({
      data: {
        user: { id: 'preview-user', email: 'preview@example.com' },
        session: {
          access_token: 'preview-token'
        }
      },
      error: null
    })
  },
  from: (table: string) => ({
    select: (_columns?: string) => {
      const mockQuery: any = { // Explicitly type mockQuery as any
        eq: () => mockQuery,
        neq: () => mockQuery,
        gte: () => mockQuery,
        lte: () => mockQuery,
        or: () => mockQuery,
        not: () => mockQuery,
        order: () => mockQuery,
        limit: () => mockQuery,
        group: () => mockQuery,
        single: async () => ({ data: getMockData(table), error: null }),
        maybeSingle: async () => ({ data: getMockData(table), error: null }),
        then: async (resolve: any) => resolve({ data: Array.isArray(getMockData(table)) ? getMockData(table) : [getMockData(table)], error: null }),
        data: Array.isArray(getMockData(table)) ? getMockData(table) : [getMockData(table)], // Ensure data is an array for consistency
        error: null,
        count: 5
      };
      
      return mockQuery;
    },
    insert: () => ({
      select: () => ({
        single: async () => ({ data: getMockData(table), error: null }),
        maybeSingle: async () => ({ data: getMockData(table), error: null }),
        data: getMockData(table),
        error: null
      }),
      single: async () => ({ data: getMockData(table), error: null }),
      maybeSingle: async () => ({ data: getMockData(table), error: null }),
      data: getMockData(table),
      error: null
    }),
    update: () => ({
      eq: () => ({
        select: () => ({
          single: async () => ({ data: getMockData(table), error: null }),
          maybeSingle: async () => ({ data: getMockData(table), error: null }),
          data: getMockData(table),
          error: null
        }),
        single: async () => ({ data: getMockData(table), error: null }),
        maybeSingle: async () => ({ data: getMockData(table), error: null }),
        data: getMockData(table),
        error: null
      }),
      select: () => ({
        single: async () => ({ data: getMockData(table), error: null }),
        maybeSingle: async () => ({ data: getMockData(table), error: null }),
        data: getMockData(table),
        error: null
      }),
      single: async () => ({ data: getMockData(table), error: null }),
      data: getMockData(table),
      error: null
    }),
    upsert: () => ({
      select: () => ({
        single: async () => ({ data: getMockData(table), error: null }),
        maybeSingle: async () => ({ data: getMockData(table), error: null }),
        data: getMockData(table),
        error: null
      }),
      single: async () => ({ data: getMockData(table), error: null }),
      data: getMockData(table),
      error: null
    }),
    delete: () => ({
      eq: () => ({
        data: null,
        error: null
      }),
      data: null,
      error: null
    })
  }),
  storage: {
    from: () => ({
      upload: async () => ({ data: { path: 'mock-path' }, error: null }),
      getPublicUrl: () => ({ data: { publicUrl: 'https://example.com/mock-image.jpg' } }),
      remove: async () => ({ data: null, error: null })
    })
  },
  rpc: async () => ({
    data: null,
    error: null
  }),
  functions: {
    invoke: async () => ({ data: null, error: null })
  }
});

// Mock data for preview mode
const getMockData = (table: string) => {
  switch (table) {
    case 'profiles':
      return {
        id: 'preview-user',
        email: 'preview@example.com',
        subscription_tier: 'abundance',
        subscription_status: 'active',
        role: 'user',
        profile_completed: true,
        display_name: 'Preview User',
        full_name: 'Preview User',
        avatar_url: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?auto=format&fit=crop&q=80&w=150'
      } as Profile;
    case 'user_progress':
      return {
        user_id: 'preview-user',
        last_visited_route: '/',
        xp: 250,
        level: 3, // Ensure level is a number
        streak_days: 7,
        preferences: {
          notifications: {
            email: true,
            push: true,
            dailyReminders: true,
            weeklyDigest: true
          },
          privacy: {
            profileVisibility: 'public',
            shareProgress: true,
            allowAiAnalysis: true
          },
          ai: {
            enhancedAnalysis: true,
            autoSuggestions: true,
            personalizedCoaching: true
          }
        }
      };
    case 'goals':
      return [];
    case 'tasks':
      return [];
    case 'analytics_events':
      return [];
    case 'user_stats':
      return [];
    case 'user_rewards':
      return [];
    default:
      return [];
  }
};

export const supabase = initSupabase();

// Add retry logic for network errors with exponential backoff
export const safeQuery = async <T>(
  query: () => Promise<T>,
  retries = 3,
  backoffMs = 1000
): Promise<T> => {
  return withRetry(query, {
    maxAttempts: retries,
    backoffMs,
    retryableErrors: [
      'Failed to fetch',
      'NetworkError',
      'network request failed',
      'Network request failed',
      'timeout',
      'socket hang up',
      'ECONNRESET'
    ],
    onRetry: (attempt, error) => {
      logger.warn(`Retrying query attempt ${attempt}`, { error });
    }
  });
};

// Helper for auth operations with retry
export const safeAuthOperation = async <T>(
  operation: () => Promise<T>,
  retries = 3
): Promise<T> => {
  return safeQuery(operation, retries);
};

// Get auth session with retry and proper error handling
export const getAuthSession = async () => {
  try {
    const result: { data: { session: any }, error: any } = await safeAuthOperation(() => supabase.auth.getSession());
    if (result.error) {
      // Handle specific auth errors
      if (result.error.message.includes('refresh_token_not_found')) {
        // Clear invalid session and redirect to login
        await supabase.auth.signOut();
        
        // Only redirect to login if not on a public page
        const currentPath = window.location.pathname;
        if (!isPublicRoute(currentPath)) {
          window.location.href = '/login';
        }
        throw new Error('Session expired. Please sign in again.');
      }
      throw new Error(result.error?.message || 'Authentication error');
    }
    return result;
  } catch (error) {
    logger.error('Error getting auth session:', error);
    throw error;
  }
};